// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/hooks/useDoorCode.ts
// 🔧 PURPOSE: Il CODICE DELLA PORTA, con la stessa verifica di sempre:
//             l'ospite scrive l'email della prenotazione, l'app la confronta
//             con quella della sessione e poi la manda al channel manager,
//             che la ricontrolla e risponde col codice (o coi codici di
//             tutti gli appartamenti, per un gruppo).
//
//             Stava dentro MyStay; adesso lo usano MyStay e la Home, con
//             la stessa logica. Il codice resta solo in memoria, in questo
//             hook: mai in localStorage, mai nella sessione. Si dimentica
//             quando l'ospite preme "Nascondi", esce o cambia prenotazione.
//
//             I codici di un gruppo arrivano SOLO da qui, cioè dopo la
//             verifica: la lista del gruppo (fetchGroupBookingInfo) li
//             contiene, ma la Home la usa solo per contare.
//
//             Dal giorno dopo la partenza non si chiede più niente (la
//             sessione dura ancora un giorno, la casa no): il server lo
//             rifiuta comunque con 403 stay_not_active.
// ============================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchAccessCode,
  type AccessCodeApartment,
  type AccessCodeResponse,
  type GuestBooking,
} from '../lib/guestApi';
import { afterCheckoutDay } from '../lib/romeDay';

/** La verifica fatta: la legge GroupCheckIn per mostrare i codici del gruppo. */
export const EMAIL_VERIFIED_KEY = 'nva_email_verified';

/** Quanto resta la spunta "copiato" accanto al codice. */
const COPIED_MS = 2000;

export interface UseDoorCodeReturn {
  /** L'email scritta dall'ospite. */
  email: string;
  /** Aggiorna l'email e cancella l'errore sotto il campo. */
  setEmail: (value: string) => void;
  /** Errore del campo email (vuota, non corrisponde). */
  emailError: string | null;
  /** Errore della richiesta al server (codice non ancora pronto, rete...). */
  error: string | null;
  loading: boolean;
  /** true dopo una verifica riuscita, finché non si nasconde. */
  revealed: boolean;
  /** La risposta del server, solo in memoria. */
  data: AccessCodeResponse | null;
  /** Il codice della casa (prenotazione singola). */
  code: string | null;
  /** I codici di tutti gli appartamenti (gruppo), o null. */
  apartments: AccessCodeApartment[] | null;
  /** Verifica l'email e chiede il codice: true se è arrivato. */
  verify: () => Promise<boolean>;
  /** Nasconde e dimentica codice ed email. */
  hide: () => void;
  /** Il codice appena copiato (per la spunta), o null. */
  copiedCode: string | null;
  copy: (code: string) => Promise<void>;
}

type DoorCodeBooking = Pick<GuestBooking, 'guestEmail' | 'checkOut'>;

export function useDoorCode(
  token: string | null | undefined,
  booking: DoorCodeBooking | null | undefined,
): UseDoorCodeReturn {
  const { t } = useTranslation();
  const [email, setEmailValue] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [data, setData] = useState<AccessCodeResponse | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const copiedTimer = useRef<number | null>(null);
  // La risposta vale solo per il token che l'ha chiesta.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const hide = useCallback(() => {
    setRevealed(false);
    setEmailValue('');
    setData(null);
  }, []);

  // Uscita o altra prenotazione: il codice di prima non si mostra più.
  useEffect(() => {
    setRevealed(false);
    setEmailValue('');
    setData(null);
    setEmailError(null);
    setError(null);
  }, [token]);

  useEffect(() => () => {
    if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
  }, []);

  const setEmail = useCallback((value: string) => {
    setEmailValue(value);
    setEmailError(null);
  }, []);

  const verify = useCallback(async (): Promise<boolean> => {
    if (!token) return false;

    setEmailError(null);

    if (!email.trim()) {
      setEmailError(t('accessCode.errors.enterEmail'));
      return false;
    }

    if (email.toLowerCase().trim() !== booking?.guestEmail?.toLowerCase()) {
      setEmailError(t('accessCode.errors.emailNoMatch'));
      return false;
    }

    // Soggiorno finito: la casa non si apre più (lo dice anche il server).
    if (afterCheckoutDay(booking?.checkOut)) {
      setError(t('myStay.home.afterStay'));
      return false;
    }

    setError(null);
    setLoading(true);

    try {
      const result = await fetchAccessCode(token, email.trim());
      if (tokenRef.current !== token) return false;

      if (result.success) {
        setData(result);
        setRevealed(true);
        // La verifica (non il codice) resta salvata: la legge GroupCheckIn.
        try {
          localStorage.setItem(EMAIL_VERIFIED_KEY, JSON.stringify({
            verified: true,
            email: email.trim().toLowerCase(),
            timestamp: new Date().toISOString(),
          }));
        } catch {
          // Storage bloccato: il codice si vede lo stesso.
        }
        return true;
      }
      setError(result.error || t('accessCode.errors.fetchFailed'));
      return false;
    } catch {
      setError(t('accessCode.errors.connectionError'));
      return false;
    } finally {
      setLoading(false);
    }
  }, [token, email, booking?.guestEmail, booking?.checkOut, t]);

  const copy = useCallback(async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedCode(value);
      if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopiedCode(null), COPIED_MS);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  const apartments = data?.isGroupBooking && data.apartments ? data.apartments : null;

  return {
    email,
    setEmail,
    emailError,
    error,
    loading,
    revealed,
    data,
    code: data?.accessCode || null,
    apartments,
    verify,
    hide,
    copiedCode,
    copy,
  };
}

export default useDoorCode;
